"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { User2, UserCircle, Package, Mail, LogOut, LayoutDashboard, LogIn, UserPlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useRouter } from "next/navigation";

interface UserAccountDropdownProps {
    onLoginClick?: () => void; // Make optional since we'll navigate instead
}

export default function UserAccountDropdown({ onLoginClick }: UserAccountDropdownProps) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    
    const { user, loading, logout } = useAuth();
    const router = useRouter();

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleLogout = async () => {
        try {
            await logout();
            setIsOpen(false);
            router.push("/");
        } catch (error) {
            console.error("Logout failed:", error);
        }
    };

    // Menu items based on user role
    const customerMenuItems = [
        { icon: UserCircle, label: "My Account", href: "/myaccount" },
        { icon: Package, label: "Orders", href: "/myaccount/orders" },
        { icon: Mail, label: "Settings", href: "/myaccount/settings" },
    ];

    const adminMenuItems = [
        { icon: LayoutDashboard, label: "Dashboard", href: "/dashboard" },
    ];

    // Show loading state
    if (loading) {
        return (
            <div className="w-9 h-9 rounded-full bg-gray-300 animate-pulse" />
        );
    }

    // NOT LOGGED IN - Show dropdown with login/signup options
    if (!user) {
        return (
            <div className="relative" ref={dropdownRef}>
                <button 
                    onMouseEnter={() => setIsOpen(true)}
                    onClick={() => setIsOpen(!isOpen)}
                    aria-label="User login"
                    className="cursor-pointer hover:text-primary transition-colors"
                >
                    <User2 className="w-5 h-5 md:w-6 md:h-6" />
                </button>

                {isOpen && (
                    <div 
                        className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50"
                        onMouseLeave={() => setIsOpen(false)}
                    >
                        {/* Login Button */}
                        <Link
                            href="/login"
                            className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-gray-700"
                            onClick={() => setIsOpen(false)}
                        >
                            <LogIn size={20} className="text-gray-600" />
                            <span className="text-sm font-medium">Login</span>
                        </Link>

                        {/* Signup Button */}
                        <Link
                            href="/signup"
                            className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-gray-700"
                            onClick={() => setIsOpen(false)}
                        >
                            <UserPlus size={20} className="text-gray-600" />
                            <span className="text-sm font-medium">Sign Up</span>
                        </Link>
                    </div>
                )}
            </div>
        );
    }

    // LOGGED IN - Show user dropdown with account options
    const getInitials = () => {
        return `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase();
    };

    const menuItems = user.role === "admin" ? adminMenuItems : customerMenuItems;

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onMouseEnter={() => setIsOpen(true)}
                onClick={() => setIsOpen(!isOpen)}
                className="w-9 h-9 rounded-full bg-primary flex items-center justify-center text-white font-semibold text-sm hover:bg-primary/90 transition-colors"
                aria-label="User account menu"
            >
                {getInitials()}
            </button>

            {isOpen && (
                <div 
                    className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50"
                    onMouseLeave={() => setIsOpen(false)}
                >
                    {/* User Info Header */}
                    <div className="px-4 py-3 border-b border-gray-200">
                        <p className="text-sm font-semibold text-gray-900">
                            {user.firstName} {user.lastName}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                        {user.role === "admin" && (
                            <span className="inline-block mt-1 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">
                                Admin
                            </span>
                        )}
                    </div>

                    {/* Menu Items */}
                    {menuItems.map((item, index) => {
                        const Icon = item.icon;
                        return (
                            <Link
                                key={index}
                                href={item.href}
                                className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-gray-700"
                                onClick={() => setIsOpen(false)}
                            >
                                <Icon size={20} className="text-gray-600" />
                                <span className="text-sm font-medium">{item.label}</span>
                            </Link>
                        );
                    })}
                    
                    <hr className="my-2 border-gray-200" />
                    
                    {/* Logout Button */}
                    <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-red-600 w-full text-left"
                    >
                        <LogOut size={20} />
                        <span className="text-sm font-medium">Logout</span>
                    </button>
                </div>
            )}
        </div>
    );
}